import * as Sentry from '@sentry/react'
import { useEffect } from 'react'
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
} from 'react-router-dom'
import { Toaster } from '@/ui/Toast/Toast'
import { ErrorBoundary } from './components/ErrorBoundary'
import { GitHubLegacySetupRedirect } from './components/github/GitHubLegacySetupRedirect'
import { ROUTES } from './constants/routes'
import { AcceptInvitePage } from './pages/AcceptInvitePage'
import { AdminPage } from './pages/AdminPage'
import { AgentFormPage } from './pages/AgentFormPage'
import { AgentProfilePage } from './pages/AgentProfilePage'
import { AgentPromptPage } from './pages/AgentPromptPage'
import { AppPage } from './pages/AppPage'
import { ChatPage } from './pages/ChatPage'
import { ForgotPasswordPage } from './pages/ForgotPasswordPage'
import { GitHubAppCallbackPage } from './pages/GitHubAppCallbackPage'
import { GitHubAppInstalledPage } from './pages/GitHubAppInstalledPage'
import { GitHubAppSetupDonePage } from './pages/GitHubAppSetupDonePage'
import { HomePage } from './pages/HomePage'
import { LoginPage } from './pages/LoginPage'
import { NewTopicPage } from './pages/NewTopicPage'
import { NewWorkspacePage } from './pages/NewWorkspacePage'
import { PersonProfilePage } from './pages/PersonProfilePage'
import { RecipesPage } from './pages/RecipesPage'
import { ResetPasswordPage } from './pages/ResetPasswordPage'
import { ResourceDetailPage } from './pages/ResourceDetailPage'
import { ResourceFormPage } from './pages/ResourceFormPage'
import { SettingsPage } from './pages/SettingsPage'
import { TopicSettingsPage } from './pages/TopicSettingsPage'
import { WaitingPage } from './pages/WaitingPage'
import { RunDetailPage, RunsPage, SkillsPage } from './pages/WorkspacePages'
import { WorkspaceResourcesPage } from './pages/WorkspaceResourcesPage'
import { WorkspaceSettingsPage } from './pages/WorkspaceSettingsPage'
import {
  applyAppearanceAttributes,
  useAppearanceStore,
  useContrastStore,
} from './store/appearance'
import { selectIsAuthenticated, useAuthStore } from './store/auth'

const TWENTY_FOUR_HOURS_S = 24 * 60 * 60

/** Decode JWT exp (seconds) without a library — JWTs are base64url-encoded JSON. */
/** Phase 6 cutover: the topic resources page became the topic view's
 * resources tab — redirect old links to the topic itself. */
function TopicResourcesRedirect() {
  const { topicId } = useParams<{ topicId: string }>()
  return <Navigate to={ROUTES.topic(topicId ?? '')} replace />
}

function getJwtExp(token: string): number | null {
  try {
    const payload = token.split('.')[1]
    const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')))
    return typeof json.exp === 'number' ? json.exp : null
  } catch {
    return null
  }
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore(selectIsAuthenticated)
  return isAuthenticated ? children : <Navigate to="/login" replace />
}

function RequireAdmin({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore(selectIsAuthenticated)
  const isSystemAdmin = useAuthStore((s) => s.isSystemAdmin)
  if (!isAuthenticated) return <Navigate to="/login" replace />
  if (!isSystemAdmin) return <Navigate to="/" replace />
  return <>{children}</>
}

/**
 * Stripe's checkout success URL targets /billing?success=1; forward the query
 * params onto the settings billing tab instead of replacing them, so the
 * success banner can actually render.
 */
function BillingRedirect() {
  const { search } = useLocation()
  const params = new URLSearchParams(search)
  params.set('tab', 'billing')
  return (
    <Navigate to={{ pathname: '/settings', search: `?${params}` }} replace />
  )
}

export function App() {
  const themeId = useAppearanceStore((s) => s.themeId)
  const textSizeId = useAppearanceStore((s) => s.textSizeId)
  const paletteId = useAppearanceStore((s) => s.paletteId)
  const contrastId = useContrastStore((s) => s.contrastId)
  const { token, refreshToken } = useAuthStore()

  // The contrast axis is its own store (its own persisted key).
  useEffect(() => {
    document.documentElement.setAttribute('data-contrast', contrastId)
  }, [contrastId])

  useEffect(() => {
    applyAppearanceAttributes(document.documentElement, {
      themeId,
      textSizeId,
      paletteId,
    })
  }, [themeId, textSizeId, paletteId])

  // Proactive token refresh: if the stored token expires within 24 hours, silently
  // fetch a new one so the user isn't interrupted mid-session.
  useEffect(() => {
    if (!token) return
    const exp = getJwtExp(token)
    if (exp === null) return
    const nowS = Math.floor(Date.now() / 1000)
    if (exp - nowS < TWENTY_FOUR_HOURS_S) {
      refreshToken().catch((e) => {
        // Keep the swallow — proactive refresh must not interrupt the user.
        // But leave a breadcrumb so a subsequent auth failure has context.
        Sentry.addBreadcrumb({
          category: 'auth',
          message: 'proactive token refresh failed',
          level: 'warning',
          data: { error: e instanceof Error ? e.message : String(e) },
        })
      })
    }
  }, [token, refreshToken])

  return (
    <>
      <ErrorBoundary standalone>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/accept-invite" element={<AcceptInvitePage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route
            path="/"
            element={
              <RequireAuth>
                <AppPage />
              </RequireAuth>
            }
          >
            {/* AppPage wraps its Outlet in an ErrorBoundary, so child routes
              don't need their own. */}
            <Route index element={<HomePage />} />
            <Route
              path="admin"
              element={
                <RequireAdmin>
                  <AdminPage />
                </RequireAdmin>
              }
            />
            <Route path="topics/:topicId" element={<ChatPage />} />
            <Route
              path="github-app/callback"
              element={<GitHubAppCallbackPage />}
            />
            <Route
              path="github-app/installed"
              element={<GitHubAppInstalledPage />}
            />
            <Route
              path="github-app/setup"
              element={<GitHubAppSetupDonePage />}
            />
            <Route
              path="topics/:topicId/settings"
              element={<TopicSettingsPage />}
            />
            {/* The topic resources page folded into the topic view's tab
              (Phase 6 cutover); old links land on the topic itself. */}
            <Route
              path="topics/:topicId/resources"
              element={<TopicResourcesRedirect />}
            />
            <Route
              path="workspaces/:workspaceId/resources"
              element={
                <GitHubLegacySetupRedirect>
                  <WorkspaceResourcesPage />
                </GitHubLegacySetupRedirect>
              }
            />
            {/* Unified resource detail — every reference deep-links here. */}
            <Route path="r/:resourceId" element={<ResourceDetailPage />} />
            <Route
              path="workspaces/:workspaceId/waiting"
              element={<WaitingPage />}
            />
            <Route
              path="workspaces/:workspaceId/skills"
              element={<SkillsPage />}
            />
            <Route path="workspaces/:workspaceId/runs" element={<RunsPage />} />
            <Route
              path="workspaces/:workspaceId/recipes"
              element={<RecipesPage />}
            />
            <Route
              path="workspaces/:workspaceId/runs/:runId"
              element={<RunDetailPage />}
            />
            <Route
              path="workspaces/:workspaceId/agents/:agentId"
              element={<AgentProfilePage />}
            />
            <Route
              path="workspaces/:workspaceId/people/:username"
              element={<PersonProfilePage />}
            />
            <Route
              path="topics/:topicId/resources/new"
              element={<ResourceFormPage />}
            />
            <Route
              path="topics/:topicId/resources/:resourceId/edit"
              element={<ResourceFormPage />}
            />
            <Route
              path="workspaces/:workspaceId/resources/new"
              element={<ResourceFormPage />}
            />
            <Route
              path="workspaces/:workspaceId/resources/:resourceId/edit"
              element={<ResourceFormPage />}
            />
            <Route path="workspaces/new" element={<NewWorkspacePage />} />
            <Route
              path="workspaces/:workspaceId/topics/new"
              element={<NewTopicPage />}
            />
            <Route path="settings" element={<SettingsPage />} />
            <Route
              path="workspaces/:workspaceId/settings"
              element={<WorkspaceSettingsPage />}
            />
            {/* The prompt editor nests so the form page stays mounted and keeps
              its unsaved state while the prompt fills the screen. */}
            <Route
              path="workspaces/:workspaceId/agents/new"
              element={<AgentFormPage />}
            >
              <Route path="prompt" element={<AgentPromptPage />} />
            </Route>
            <Route
              path="workspaces/:workspaceId/agents/:agentId/edit"
              element={<AgentFormPage />}
            >
              <Route path="prompt" element={<AgentPromptPage />} />
            </Route>
            <Route path="billing" element={<BillingRedirect />} />
          </Route>
        </Routes>
      </ErrorBoundary>
      {/* Root-level so globally-raised toasts (query errors, unhandled
          rejections) are visible on auth routes too. */}
      <Toaster />
      <div data-grain aria-hidden="true" />
    </>
  )
}
